<script setup lang="ts">
import { RouterLink } from 'vue-router'

import BaseDrawer from '@/components/base/BaseDrawer.vue'
import BaseIcon from '@/components/base/BaseIcon.vue'
import { useActiveNavItem } from '@/composables/navigation/use-active-nav-item.js'
import type { NavSection } from '@/constants/app/navigation.constant.js'

/**
 * The slide-up menu behind "More": every section the tab bar could not fit, with its
 * screens listed under it. Each row is a 44 px target — it is tapped with a thumb.
 *
 * A sheet rather than a second bar: the overflow is a different size for every role, and
 * a bar that overflows the overflow is the thing this replaces.
 */
defineProps<{ open: boolean; sections: readonly NavSection[] }>()
const emit = defineEmits<{ close: [] }>()

const active = useActiveNavItem()
</script>

<template>
  <BaseDrawer
    :open="open"
    side="bottom"
    title="More"
    @close="emit('close')"
  >
    <nav
      class="flex flex-col gap-5"
      aria-label="More sections"
    >
      <section
        v-for="section in sections"
        :key="section.id"
      >
        <p class="mb-1 flex items-center gap-2 px-2 text-micro text-ink-muted">
          <BaseIcon
            :name="section.icon"
            :size="14"
          />
          {{ section.label }}
        </p>
        <ul class="flex flex-col">
          <li
            v-for="item in section.items"
            :key="item.label"
          >
            <RouterLink
              v-if="item.available && item.route !== null"
              :to="{ name: item.route, query: item.query ?? {} }"
              :class="[
                'flex min-h-11 items-center justify-between rounded-control px-2 text-body transition-colors duration-(--motion-fast)',
                active(item) ? 'bg-nav-active font-medium text-action' : 'text-ink hover:bg-sunken',
              ]"
              :aria-current="active(item) ? 'page' : undefined"
              @click="emit('close')"
            >
              {{ item.label }}
              <BaseIcon
                name="caret-right"
                :size="16"
                class="text-ink-muted"
              />
            </RouterLink>
            <span
              v-else
              class="flex min-h-11 cursor-not-allowed items-center justify-between rounded-control px-2 text-body text-ink-disabled"
              :title="`${item.label} arrives in a later slice`"
              :aria-disabled="true"
            >
              {{ item.label }}
              <span class="sr-only">— not built yet</span>
              <BaseIcon
                name="lock-simple"
                :size="14"
              />
            </span>
          </li>
        </ul>
      </section>
    </nav>
  </BaseDrawer>
</template>
